import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
  FiClock, FiChevronDown, FiPackage, FiCheckCircle, FiXCircle, FiAlertCircle, FiTruck, FiFileText,
} from 'react-icons/fi';

import { SpaceService } from '../../../support/services/api';
import styles from './OrdersCard.module.css';

function OrdersCard({ order }) {
  const [opened, setOpened] = useState(0);
  const [producer, setProducer] = useState();
  const [consumer, setConsumer] = useState();

  const formatDeliveryDate = (deliveryDate) => {
    const parsedDate = new Date(deliveryDate);
    const correctedDeliveryDate = new Date(parsedDate.valueOf() + parsedDate.getTimezoneOffset() * 60 * 1000);
    return format(correctedDeliveryDate, 'dd/MM/yyyy');
  };

  const fetchData = useCallback(async () => {
    if (!producer && order) {
      const producersIds = [order.producerId];

      if (order.consumerType === 'producer') {
        producersIds.push(order.consumerId);
      }

      const response = await SpaceService().getByIds(producersIds, true);

      setProducer(response.producers.find((prod) => prod.id === order.producerId));
      setConsumer(response.producers.find((prod) => prod.id === order.consumerId));
    }
  }, [producer, order]);

  useEffect(() => { fetchData(); }, []);

  
  return (
    <div className={styles.card}>
      <div className={styles.divisory}>
        <div className={styles.divisoryLine} />
      </div>

      <div className={styles.cardInfo}>
        <div className={styles.dataLeft}>
          <div className={styles.statusIcon}>
            {{
              waiting_confirmation: <FiClock size={32} strokeWidth="1" color="#2d6fad" />,
              confirmed: <FiFileText size={32} strokeWidth="1" color="#2d6fad" />,
              waiting_delivery: <FiPackage size={32} strokeWidth="1" color="#2d6fad" />,
              waiting_payment: <FiAlertCircle size={32} strokeWidth="1" color="#c50000" />,
              delivering: <FiTruck size={32} strokeWidth="1" color="#2d6fad" />,
              delivered: <FiCheckCircle size={32} strokeWidth="1" color="#168500" />,
              canceled: <FiXCircle size={32} strokeWidth="1" color="#c50000" />,
            }[order.status]}
          </div>

          <div>
            <p className={styles.orderStatus}>
              {`#${order.id} - `}
              {{
                waiting_confirmation: 'A confirmar',
                confirmed: 'Confirmado',
                waiting_delivery: 'Pronto para entrega',
                waiting_payment: 'Aguardando pagamento',
                delivering: 'Entregando',
                delivered: 'Entregue',
                canceled: 'Cancelado',
              }[order.status]}
            </p>
            <h2 className={styles.orderTitle}>
              {producer ? producer.name : '-'}
            </h2>
            <h3 className={styles.orderToCompany}>
              {
                order.consumerType === 'producer' && consumer
                && (<span className={styles.to}>{`para ${consumer.name ?? consumer?.register?.producerName ?? consumer?.register?.companyName}`}</span>)
              }
            </h3>

            <p className={styles.deliveryMethod}>
              {{
                home_delivery: `Entrega ${order.consumerType === 'producer' ? 'na empresa' : 'em domicílio'}`,
                grab_and_go: 'Retirada no local',
              }[order.delivery.method]}
            </p>
            <p className={styles.orderDate}>
              {`${formatDeliveryDate(order.delivery.date)} - ${
                {
                  morning: 'manhã',
                  afternoon: 'tarde',
                  night: 'noite',
                }[order.delivery.shift]
              }`}
            </p>
          </div>
        </div>

        <div className={styles.dataRight}>
          <div>
            <span className={styles.paymentMethod}>
              {{
                debit_card: 'Débito / ',
                credit_card: 'Crédito / ',
                billet: 'Boleto / ',
                cash: 'Dinheiro / ',
                bank_transfer: 'Transferência / ',
              }[order.payment.method]}
            </span>
            <span className={`${styles.paymentStatus} ${order.payment.status === 'paid' ? '' : styles.unpaid}`}>
              {{
                paid: 'pago',
                unpaid: 'não pago',
              }[order.payment.status]}
            </span>
          </div>
          <p className={styles.orderValue}>
            {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
              .format(order.totalValue + order.delivery.value + (order.donation?.value ?? 0))}
          </p>
        </div>

        <button type="button" className={styles.detailsButton} onClick={() => setOpened(!opened)}>
          <p>Detalhes</p>
          <FiChevronDown size={14} strokeWidth={2} />
        </button>
      </div>

      {
        opened && order
          ? (
            <div className={styles.cardDetails}>
              <OrderItensList items={order.items} />
              <OrderItensList items={order.items} scheduled />
              <p>
                <span className={styles.donation}>
                  Doação
                </span>
                <br />
                <span className={styles.donation}>+ </span>
                {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(order.donation?.value ?? 0)}
                {' '}
              </p>
              <p>
                <span>
                  {{
                    home_delivery: 'Entrega em ',
                    grab_and_go: 'Retirada em ',
                  }[order.delivery.method]}
                </span>
                {order.delivery.address.street}
                ,
                {order.delivery.address.number}
                {order.delivery.address.complement ? ` (${order.delivery.address.complement})` : ''}
                ,
                {' '}
                {order.delivery.address.neighborhood}
                .
                {' '}
                {order.delivery.address.city}
                {' '}
                -
                {' '}
                {order.delivery.address.uf}

                <br />
                <span>+ </span>
                {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(order.delivery.value)}
                {' '}
                - Entrega
              </p>
            </div>
          )
          : <></>
      }

    </div>
  );
}

OrdersCard.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
    consumerId: PropTypes.string.isRequired,
    producerId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    createdAt: PropTypes.instanceOf(Date),
    updatedAt: PropTypes.instanceOf(Date),
    items: PropTypes.arrayOf(PropTypes.shape({
      amount: PropTypes.number,
      value: PropTypes.number,
      offerId: PropTypes.number,
      offerMeasureQuantity: PropTypes.number,
      offerMeasureType: PropTypes.string,
      offerValue: PropTypes.number,
      offerAmount: PropTypes.number,
      offerType: PropTypes.string,
      productId: PropTypes.number,
      productName: PropTypes.string,
      productDescription: PropTypes.string,
    })),
    delivery: PropTypes.shape({
      method: PropTypes.string,
      date: PropTypes.number,
      value: PropTypes.number,
      shift: PropTypes.string,
      address: PropTypes.shape({
        street: PropTypes.string,
        number: PropTypes.string,
        complement: PropTypes.string,
        neighborhood: PropTypes.string,
        city: PropTypes.string,
        uf: PropTypes.string,
      }),
    }),
    payment: PropTypes.shape({
      method: PropTypes.string,
      status: PropTypes.string,
    }),
  }).isRequired,
};

export default OrdersCard;

const OrderItensList = ({ items, scheduled }) => {
  const filteredItems = items.filter((item) => (scheduled ? (item.offerType === 'scheduled_delivery') : (item.offerType !== 'scheduled_delivery')));

  if (!filteredItems.length) return <></>;
  return (
    <>
      <p className={styles[scheduled ? 'scheduled_delivery' : 'prompt_delivery']}>{scheduled ? 'Encomenda' : 'Pronta entrega'}</p>
      <ul className={styles.orderItems}>
        {
          filteredItems.map((item) => (
            <li key={item.offerId}>
              <span className={styles.prompt_delivery}>+ </span>
              {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.totalValue)}
              {` - ${item.availableStock} x `}
              {item.name.length > 15 ? `${item.name.substring(0, 15)}...` : item.name}
              {` (${item.measureQuantity} ${item.measureType})`}
            </li>
          ))
        }
      </ul>
    </>
  );
};
